import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Table,
  Space,
  Modal,
  Button,
  Form,
  Input,
  Row,
  Col,
  Select,
  Tag,
  Popconfirm,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { EditOutlined, CheckOutlined, PrinterOutlined, SearchOutlined } from '@ant-design/icons';
import Highlighter from 'react-highlight-words';
import moment from 'moment';

import {
  QaqcAnalysisService,
} from '@gtpl/shared-services/raw-material-procurement';

import { ProductService, SkuService } from '@gtpl/shared-services/masters';
import { QualityTypeEnum } from '@gtpl/shared-models/common-models';
import { AlertMessages } from '@gtpl/shared-utils/alert-messages';
import QAQCProductsCount from './qaqc-products-count';
import { QAQCProductCountPrint } from './qaqc-product-count-print';
import { Link } from 'react-router-dom';
import { Excel } from 'antd-table-saveas-excel';

const { Option } = Select;

interface ProductCountItem {
  id: number;
  testDate: string;
  processType: string;
  lotNumber: string;
  operation: string;
  productShortCode: string;
  quality: string;
  quantity: string;
  productType: string;
  weightInGms: number;
  count: number;
  isQAQCApproved: number;
  createdAt: string;
}

interface GroupedProductCount {
  lotNumber: string;
  operation: string;
  productShortCode: string;
  quality: string;
  quantity: string;
  testDate: string;
  items: ProductCountItem[];
  isQAQCApproved: number;
}

const QAQCProductsCountView: React.FC = () => {
  const [productCounts, setProductCounts] = useState<ProductCountItem[]>([]);
  const [groupedProductCounts, setGroupedProductCounts] = useState<GroupedProductCount[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<GroupedProductCount | null>(null);

  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');
  const searchInput = useRef<any>(null);

  const [productsData, setProductsData] = useState<any[]>([]);
  const [lotNumDropdownData, setLotNumDropdownData] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPageSize, setCurrentPageSize] = useState(10);

  const [filterForm] = Form.useForm();
  const [showEditComponent, setShowEditComponent] = useState(false);
  const [showPrintComponent, setShowPrintComponent] = useState(false);

  const qaqcService = new QaqcAnalysisService();
  const productService = new ProductService();
  const skucodeservice = new SkuService();
  useEffect(() => {
    fetchData();
    getAllProducts();
    getLotsDropdownForQAQC();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const req: any = {};

      if (filterForm.getFieldValue('variant')) {
        req.product = filterForm.getFieldValue('variant');
      }

      if (filterForm.getFieldValue('quality')) {
        req.quality = filterForm.getFieldValue('quality');
      }

      if (filterForm.getFieldValue('lotNo')) {
        req.lotNumber = filterForm.getFieldValue('lotNo');
      }

      const res = await qaqcService.getProductCounts(req);

      if (res.status) {
        setProductCounts(res.data);
        
        // Group records by lot number
        const groupedMap = new Map<string, GroupedProductCount>();
        
        res.data.forEach((record: ProductCountItem) => {
          const key = `${record.lotNumber}-${record.testDate}`;
          
          if (!groupedMap.has(key)) {
            groupedMap.set(key, {
              lotNumber: record.lotNumber,
              operation: record.operation,
              productShortCode: record.productShortCode,
              quality: record.quality,
              quantity: record.quantity,
              testDate: record.testDate,
              items: [],
              isQAQCApproved: record.isQAQCApproved,
            });
          }
          
          groupedMap.get(key)!.items.push(record);
        });
        
        setGroupedProductCounts(Array.from(groupedMap.values()));
      } else {
        AlertMessages.getErrorMessage(res.internalMessage);
        setProductCounts([]);
        setGroupedProductCounts([]);
      }
    } catch (error: any) {
      AlertMessages.getErrorMessage(error.message);
    } finally {
      setLoading(false);
    }
  };

  const onReset = () => {
    filterForm.resetFields();
    fetchData();
  };

  const openEditModal = (record: GroupedProductCount) => {
    setSelectedRecord(record);
    setShowEditComponent(true);
  };

  const closeEditModal = () => {
    setShowEditComponent(false);
    setSelectedRecord(null);
  };

  const openPrintModal = (record: GroupedProductCount) => {
    setSelectedRecord(record);
    setShowPrintComponent(true);
  };

  const closePrintModal = () => {
    setShowPrintComponent(false);
    setSelectedRecord(null);
  };

  const onApprove = async (record: GroupedProductCount) => {
    try {
      // Send all IDs for the lot to approve
      const ids = record.items.map(item => item.id);
      
      const payload = {
        ids: ids
      };

      const res = await qaqcService.approveProductCounts(payload);

      if (res.status) {
        AlertMessages.getSuccessMessage('Product count approved successfully');
        fetchData();
      } else {
        AlertMessages.getErrorMessage(res.internalMessage);
      }
    } catch (error: any) {
      AlertMessages.getErrorMessage(error.message);
    }
  };

 const getAllProducts= () =>{
  skucodeservice.getActiveSkuCodes().then(res =>{
    if(res.status){
      setProductsData(res.data);
    }else {
      if(res.intlCode){
        AlertMessages.getErrorMessage(res.internalMessage);
      }else{
        AlertMessages.getErrorMessage(res.internalMessage);
        setProductsData([])
      }
    }
  }).catch(err => {
    AlertMessages.getErrorMessage(err.message);
  })

}

  const getLotsDropdownForQAQC = () => {
    qaqcService.getLotsDropdownForQAQC().then(res => {
      if (res.status) setLotNumDropdownData(res.data);
      else setLotNumDropdownData([]);
    });
  };

const handleProductCountTableChange = (pagination: any) => {
  setCurrentPage(pagination.current || 1);
  setCurrentPageSize(pagination.pageSize || 10);
};
  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleResetSearch = (clearFilters) => {
    clearFilters();
    setSearchText('');
  };
  function handleReset(clearFilters) {
    clearFilters();
    setSearchText('');
  };

  const getColumnSearchProps = (dataIndex: string) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ width: 188, marginBottom: 8, display: 'block' }}
        />
        <Button
          type="primary"
          onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
          icon={<SearchOutlined />}
          size="small"
          style={{ width: 90, marginRight: 8 }}
        >
          Search
        </Button>
        <Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}>
          Reset
        </Button>
      </div>
    ),
    filterIcon: filtered => (
      <SearchOutlined type="search" style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex]
          .toString()
          .toLowerCase()
          .includes(value.toLowerCase())
        : false,
    onFilterDropdownVisibleChange: visible => {
      if (visible) { setTimeout(() => searchInput.current.select()); }
    },
    render: text =>
      text ? (
        searchedColumn === dataIndex ? (
          <Highlighter
            highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
            searchWords={[searchText]}
            autoEscape
            textToHighlight={text.toString()}
          />
        ) : text
      ) : null
  });

  const productCountColumns: ColumnsType<GroupedProductCount> = [
    {
      title: 'S No',
      key: 'sno',
      width: 70,
      render: (_text, _record, index) => (index + 1) + ((currentPage - 1) * currentPageSize),
    },
    {
      title: 'Test Date',
      dataIndex: 'testDate',
      
      sorter: (a, b) => moment(a.testDate).unix() - moment(b.testDate).unix(),
      ...getColumnSearchProps('testDate'),
      render: (date) => moment(date).format('DD-MM-YYYY'),
    },
    {
      title: 'Lot Number',
      dataIndex: 'lotNumber',
      sorter: (a, b) => (a.lotNumber || '').localeCompare(b.lotNumber || ''),
      ...getColumnSearchProps('lotNumber'),
    },
    {
      title: 'Operation',
      dataIndex: 'operation',
            sorter: (a, b) => (a.operation || '').localeCompare(b.operation || ''),

      ...getColumnSearchProps('operation'),
    },
    {
      title: 'Product',
      dataIndex: 'productShortCode',
      sorter: (a, b) => (a.productShortCode || '').localeCompare(b.productShortCode || ''),
      ...getColumnSearchProps('productShortCode'),
    },
    {
      title: 'Quality',
      dataIndex: 'quality',
        sorter: (a, b) => (a.quality || '').localeCompare(b.quality || ''),
      render: text => text ? text :'-'
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
        sorter: (a, b) => (a?.quantity || '')?.localeCompare(b?.quantity || ''),
        render: (value) => value ? Number(Number(value).toFixed(2)) : '-',    },
    
    {
      title: 'QAQC Status',
      dataIndex: 'isQAQCApproved',
      
      render: (isApproved: number) => (
        <Tag color={isApproved === 1 ? 'green' : 'orange'}>
          {isApproved === 1 ? 'Approved' : 'Pending'}
        </Tag>
      ),
    },
    {
      title: 'Action',
      render: (_, record) => (
        <Space>
          {record.isQAQCApproved === 0 ? (
            <>
              <Button 
                type="link" 
                icon={<EditOutlined />} 
                onClick={() => openEditModal(record)} 
              />
              <Popconfirm
                title="Are you sure you want to approve this product count?"
                onConfirm={() => onApprove(record)}
                okText="Yes"
                cancelText="No"
              >
                <Button type="link" icon={<CheckOutlined />} />
              </Popconfirm>
            </>
          ) : (
            <Button 
              type="link" 
              icon={<PrinterOutlined />} 
              onClick={() => openPrintModal(record)}
            >
              Print
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const exportExcel = () => {
    if (!groupedProductCounts.length) {
      message.warning('No data to export');
      return;
    }

    const summaryColumns = [
      { title: 'Test Date', dataIndex: 'testDate' },
      { title: 'Lot Number', dataIndex: 'lotNumber' },
      { title: 'Operation', dataIndex: 'operation' },
      { title: 'Product', dataIndex: 'product' },
      { title: 'Quality', dataIndex: 'quality' },
      { title: 'Quantity', dataIndex: 'quantity' },
      { title: 'QAQC Status', dataIndex: 'qaqcStatus' },
    ];

    const summaryRows = groupedProductCounts.map((r) => ({
      testDate: moment(r.testDate).format('DD-MM-YYYY'),
      lotNumber: r.lotNumber ?? '',
      operation: r.operation ?? '',
      product: r.productShortCode ?? '',
      quality: r.quality ? String(r.quality) : '-',
      quantity: r.quantity || '-',
      qaqcStatus: r.isQAQCApproved === 1 ? 'Approved' : 'Pending',
    }));

    const itemColumns = [
      { title: 'Test Date', dataIndex: 'testDate' },
      { title: 'Lot Number', dataIndex: 'lotNumber' },
      { title: 'Operation', dataIndex: 'operation' },
      { title: 'Product', dataIndex: 'productShortCode' },
      { title: 'Quality', dataIndex: 'quality' },
      { title: 'Quantity', dataIndex: 'quantity' },
      { title: 'Process Type', dataIndex: 'processType' },
      { title: 'Weight (gms)', dataIndex: 'weightInGms' },
      { title: 'Count', dataIndex: 'count' },
    ];

    const itemRows: Record<string, string | number>[] = [];
    groupedProductCounts.forEach((g) => {
      g.items.forEach((item) => {
        itemRows.push({
          testDate: moment(g.testDate).format('DD-MM-YYYY'),
          lotNumber: g.lotNumber ?? '',
          operation: g.operation ?? '',
          product: g.productShortCode ?? '',
          quality: g.quality ? String(g.quality) : '-',
          quantity: g.quantity || '-',
          processType: item.processType ?? '',
          weightInGms: item.weightInGms != null ? `${item.weightInGms}g` : '',
          count: item.count ?? '',
        });
      });
    });

    const excel = new Excel();
    const summarySheet = excel.addSheet('Product Counts');
    summarySheet.addColumns(summaryColumns);
    summarySheet.addDataSource(summaryRows, { str2num: true });

    const itemsSheet = excel.addSheet('Product Count Items');
    itemsSheet.addColumns(itemColumns);
    itemsSheet.addDataSource(itemRows, { str2num: true });

    excel.saveAs('QAQC-Product-Counts.xlsx');
  };

  return (
    <Card 
      title="Product Counts View" 
      style={{ margin: 20 }}
      headStyle={{
        backgroundColor: '#69c0ff',
        border: 0,
        textAlign: 'center',
      }}
      extra={
        <>
          <Button type="primary" onClick={exportExcel} style={{ marginRight: 8 }}>
            Get Excel
          </Button>
          <Link to="/qaqc-products-count">
            <span style={{ color: 'white' }}>
              <Button type="primary">Create</Button>
            </span>
          </Link>
        </>
      }
    >
      <Form form={filterForm} onFinish={fetchData} layout="inline" style={{ marginBottom: 16 }}>
        <Row gutter={16} style={{ width: '100%' }}>
          <Col xs={{ span: 6 }} sm={{ span: 6 }} md={{ span: 6 }} lg={{ span: 6 }} xl={{ span: 6 }}>
            <Form.Item name="variant" label="Variant">
              <Select 
                allowClear
                showSearch
                placeholder='Select Variant'
                filterOption={(input, option) =>
                  option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                 {productsData.map(p => (
                      <Option key={p.skuCodeId} value={p.skuCodeId}>
                        {p.shortCode}
                      </Option>
                    ))}
              </Select>
            </Form.Item>
          </Col>

          <Col xs={{ span: 6 }} sm={{ span: 6 }} md={{ span: 6 }} lg={{ span: 6 }} xl={{ span: 6 }}>
            <Form.Item name="lotNo" label="Lot No">
              <Select 
                allowClear
                showSearch
                placeholder='Select Lot No'
                filterOption={(input, option) =>
                  option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {lotNumDropdownData.map(l => (
                  <Option key={l.lotNumber} value={l.lotNumber}>
                    {l.lotNumber}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col xs={{ span: 6 }} sm={{ span: 6 }} md={{ span: 6 }} lg={{ span: 6 }} xl={{ span: 6 }}>
            <Form.Item name="quality" label="Quality">
              <Select 
                allowClear
                showSearch
                placeholder='Select Quality'
                filterOption={(input, option) =>
                  option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {Object.values(QualityTypeEnum).map(q => (
                  <Option key={q} value={q}>{q}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
       
          <Col style={{ textAlign: 'right', paddingTop: '0px' }}>
            <Button type="primary" htmlType="submit">
              Search
            </Button>
            <Button htmlType="button" style={{ margin: '0 14px' }} onClick={onReset}>
              Reset
            </Button>
          </Col>
        </Row>
      </Form>

      <Table
        columns={productCountColumns}
        dataSource={groupedProductCounts}
        loading={loading}
        rowKey={(record) => `${record.lotNumber}-${record.testDate}`}
        scroll={{ x: 1400 }}
        pagination={{
          current: currentPage,
          pageSize: currentPageSize,
        }}
        onChange={handleProductCountTableChange}
        bordered
        expandable={{
          expandedRowRender: (record: GroupedProductCount) => (
            <div style={{ padding: '16px' }}>
              <h4>Product Count Items</h4>
              <Table
                columns={[
                  {
                    title: 'Process Type',
                    dataIndex: 'processType',
                    key: 'processType',
                  },
                  {
                    title: 'Weight (gms)',
                    dataIndex: 'weightInGms',
                    key: 'weightInGms',
                    render: (weight) => `${weight}g`,
                  },
                  {
                    title: 'Count',
                    dataIndex: 'count',
                    key: 'count',
                  },
                  
                ]}
                dataSource={record.items}
                rowKey="id"
                pagination={false}
                size="small"
              />
            </div>
          ),
          rowExpandable: (record) => record.items.length > 0,
        }}
      />

      {showEditComponent && selectedRecord && (
        <Modal
          visible={showEditComponent}
          onCancel={closeEditModal}
          footer={null}
          width={900}
          title="Edit Product Count"
          destroyOnClose
          style={{top:10}}
        >
          <QAQCProductsCount
            editRecord={selectedRecord}
            isEditMode={true}
            onClose={() => {
              closeEditModal();
              fetchData();
            }}
          />
        </Modal>
      )}

      {showPrintComponent && selectedRecord && (
        <Modal
          visible={showPrintComponent}
          onCancel={closePrintModal}
          footer={null}
          width={1000}
          title="Product Count Print"
          destroyOnClose
          style={{top:10}}
        >
          <QAQCProductCountPrint record={selectedRecord} />
        </Modal>
      )}
    </Card>
  );
};

export default QAQCProductsCountView;
